import { API_BASE_URL, browserSafeFetch } from "./api-base.ts";
import { withCommunityRequestDeadline, COMMUNITY_READ_TIMEOUT_MS } from "./community-request-deadline.ts";
import type { AuthTokenSource } from "./api.ts";
import {
  cloneStrategyRuleDSL,
  strategyRuleDSLIdentity,
  type StrategyRuleDSL,
} from "./strategy-builder.ts";
import type {
  BacktestResult,
  BacktestSetupSnapshot,
  HistoryItem,
} from "./types.ts";
import { resolveSharedResultLocale } from "../shared-result-contract.ts";
import { sharedResultText } from "../shared-result-i18n.ts";
import { isStrategyPresetName } from "./strategy-presets.ts";

const OMITTED_FIELDS = [
  "account_and_auth_data",
  "backend_and_repository_ids",
  "exact_strategy_rules_and_prompt",
  "fingerprints_and_private_notes",
  "raw_candles_and_trades",
  "starting_capital_and_usage_data",
] as const;

const PERIODS = new Set(["1M", "3M", "6M", "1Y", "Custom"]);
const EXECUTION_MODELS = new Set(["spot", "futures"]);
const MARGIN_MODES = new Set(["isolated", "cross"]);
const TRADE_DIRECTIONS = new Set(["long", "short", "both"]);
const EXIT_MODES = new Set(["tp_sl_only", "signal_and_risk", "signal_only"]);

export type ShareMode = "summary_only" | "reproducible_strategy";

export interface FrozenReproducibleShareSource {
  historyItemID: string;
  result: BacktestResult;
  setup: BacktestSetupSnapshot;
  strategyDSL: StrategyRuleDSL;
  testedPeriod: { start: string; end: string };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function safeText(value: string | null | undefined, fallback: string, maximum: number): string {
  const normalized = Array.from((value || "").trim())
    .filter((character) => !/[\p{Cc}\p{Cf}\p{Cs}]/u.test(character))
    .join("")
    .slice(0, maximum);
  if (!normalized || /(?:https?:\/\/|www\.|[^\s@]+@[^\s@]+\.[^\s@]+)/iu.test(normalized)) {
    return fallback;
  }
  return normalized;
}

function normalizedCurve(result: BacktestResult): Array<{ progress: number; return_percent: number }> {
  const source = result.equity_curve || [];
  if (source.length < 2) return [];
  const initial = Number(source[0]?.equity);
  if (!Number.isFinite(initial) || initial <= 0) return [];
  const maximum = 48;
  const indexes = source.length <= maximum
    ? source.map((_, index) => index)
    : Array.from({ length: maximum }, (_, index) => Math.round(index * (source.length - 1) / (maximum - 1)));
  return [...new Set(indexes)].map((sourceIndex, index, values) => ({
    progress: values.length === 1 ? 0 : index / (values.length - 1),
    return_percent: (Number(source[sourceIndex]?.equity) / initial - 1) * 100,
  })).filter((point) => Number.isFinite(point.return_percent));
}

function exactISOInstant(value: unknown): string | null {
  if (typeof value !== "string" || !value || value !== value.trim()) return null;
  return Number.isFinite(Date.parse(value)) ? value : null;
}

/**
 * The public evidence period is the data that was actually tested, not the
 * wider date range that was requested. Every candle must be strictly ordered;
 * otherwise choosing the first and last element would publish false evidence.
 */
function exactTestedPeriod(result: BacktestResult): { start: string; end: string } | null {
  if (!Array.isArray(result.candles) || result.candles.length < 2) return null;
  let previous = Number.NEGATIVE_INFINITY;
  let start = "";
  let end = "";
  for (const [index, candle] of result.candles.entries()) {
    const time = exactISOInstant(candle?.time);
    if (!time) return null;
    const instant = Date.parse(time);
    if (instant <= previous) return null;
    previous = instant;
    if (index === 0) start = time;
    end = time;
  }
  return start && end ? { start, end } : null;
}

function exactResultIdentity(result: BacktestResult): string | null {
  if (!isObject(result) || typeof result.symbol !== "string" || typeof result.timeframe !== "string") return null;
  const runID = typeof result.run_id === "string" ? result.run_id.trim() : "";
  const generatedAt = exactISOInstant(result.generated_at);
  if (!runID || !generatedAt || !result.symbol?.trim() || !result.timeframe?.trim()) return null;
  return JSON.stringify([
    runID,
    generatedAt,
    result.symbol.trim().toUpperCase(),
    result.timeframe.trim().toLowerCase(),
  ]);
}

/** Legacy summaries may link only to the setup saved with this exact completed run. */
export function legacyShareSetupURL(result: BacktestResult, history: readonly HistoryItem[], locale: string): string | null {
  const identity = exactResultIdentity(result);
  if (!identity) return null;
  const matches = history.filter((item) => isObject(item) && isObject(item.setup) && isObject(item.result)
    && exactResultIdentity(item.result as BacktestResult) === identity);
  if (matches.length !== 1) return null;
  const setup = matches[0].setup;
  if (typeof setup.symbol !== "string" || setup.symbol !== result.symbol
    || typeof setup.timeframe !== "string" || setup.timeframe !== result.timeframe
    || !PERIODS.has(setup.backtestPeriod || "") || !TRADE_DIRECTIONS.has(setup.tradeDirection || "")
    // The legacy route cannot restore custom dates, Futures, Builder rules or
    // private prompts. Keep those shares as summaries instead of a wrong setup.
    || setup.backtestPeriod === "Custom" || setup.executionModel !== "spot"
    || setup.strategyMode !== "auto" || !isStrategyPresetName(setup.strategyName)) return null;
  const params = new URLSearchParams({ symbol: setup.symbol, timeframe: setup.timeframe,
    period: setup.backtestPeriod!, direction: setup.tradeDirection!, mode: setup.strategyMode!,
    strategy: setup.strategyName });
  return `https://backtest.click/${resolveSharedResultLocale(locale)}/backtest?${params.toString()}`;
}

function finiteSetupNumber(setup: BacktestSetupSnapshot, key: keyof BacktestSetupSnapshot): number | null {
  const value = setup[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function frozenAssumptions(
  setup: BacktestSetupSnapshot,
  testedPeriod: { start: string; end: string },
) {
  const commissionPercent = finiteSetupNumber(setup, "commissionPercent");
  const slippageBps = finiteSetupNumber(setup, "slippageBps");
  const positionSizePercent = finiteSetupNumber(setup, "positionSizePercent");
  const initialCapital = finiteSetupNumber(setup, "initialCapital");
  const leverage = finiteSetupNumber(setup, "leverage");
  const maintenanceMarginPercent = finiteSetupNumber(setup, "maintenanceMarginPercent");
  const fundingRatePercent = finiteSetupNumber(setup, "fundingRatePercent");
  const fundingIntervalHours = finiteSetupNumber(setup, "fundingIntervalHours");
  const liquidationFeePercent = finiteSetupNumber(setup, "liquidationFeePercent");
  const stopLossPercent = finiteSetupNumber(setup, "stopLossPercent");
  const takeProfitPercent = finiteSetupNumber(setup, "takeProfitPercent");
  if (
    commissionPercent === null
    || slippageBps === null
    || positionSizePercent === null
    || initialCapital === null
    || leverage === null
    || maintenanceMarginPercent === null
    || fundingRatePercent === null
    || fundingIntervalHours === null
    || liquidationFeePercent === null
    || stopLossPercent === null
    || takeProfitPercent === null
    || !PERIODS.has(setup.backtestPeriod || "")
    || !EXECUTION_MODELS.has(setup.executionModel || "")
    || !MARGIN_MODES.has(setup.marginMode || "")
    || !TRADE_DIRECTIONS.has(setup.tradeDirection || "")
    || !EXIT_MODES.has(setup.exitMode || "")
  ) return null;
  return {
    commission_percent: commissionPercent,
    slippage_bps: slippageBps,
    position_size_percent: positionSizePercent,
    execution_model: setup.executionModel,
    margin_mode: setup.marginMode,
    leverage: setup.executionModel === "futures" ? leverage : 1,
    maintenance_margin_percent: maintenanceMarginPercent,
    funding_rate_percent: setup.executionModel === "futures" ? fundingRatePercent : 0,
    funding_interval_hours: fundingIntervalHours,
    liquidation_fee_percent: liquidationFeePercent,
    trade_direction: setup.tradeDirection,
    exit_mode: setup.exitMode,
    stop_loss_percent: stopLossPercent,
    take_profit_percent: takeProfitPercent,
    period: setup.backtestPeriod,
    // Override the requested/clamped setup range with the exact first and last
    // candles used by the completed run.
    start_date: testedPeriod.start,
    end_date: testedPeriod.end,
  };
}

function safeStrategyIdentity(value: StrategyRuleDSL | undefined): string {
  try {
    return strategyRuleDSLIdentity(value);
  } catch {
    return "";
  }
}

/** A server-provided recipe comes from the executed preset, never a name or the current form. */
function executedSharingRecipe(result: BacktestResult): StrategyRuleDSL | null {
  const recipe = result.sharing_recipe;
  if (!recipe || recipe.schema_version !== 1 || recipe.source !== "executed_strategy"
    || recipe.catalog_version !== "2026-07-22" || !/^[a-f0-9]{64}$/.test(recipe.strategy_fingerprint)
    || recipe.strategy_dsl?.schema_version !== 2 || recipe.strategy_dsl.catalog_version !== recipe.catalog_version
    || recipe.strategy_dsl.short_mode !== "mirror_long" || recipe.strategy_dsl.risk?.mode !== "inherit"
    || !safeStrategyIdentity(recipe.strategy_dsl)) return null;
  return recipe.strategy_dsl;
}

/**
 * Resolves the only source authorized for a reproducible share: one complete
 * local HistoryItem whose immutable run identity matches the displayed result.
 * Legacy/cloud placeholders and ambiguous duplicate runs fail closed.
 */
export function resolveFrozenReproducibleShareSource(
  displayedResult: BacktestResult,
  history: readonly HistoryItem[],
): FrozenReproducibleShareSource | null {
  const displayedIdentity = exactResultIdentity(displayedResult);
  if (!displayedIdentity) return null;
  const matches = history.filter((item) => (
    isObject(item)
    && isObject(item.setup)
    && isObject(item.result)
    && exactResultIdentity(item.result as BacktestResult) === displayedIdentity
  ));
  if (matches.length !== 1) return null;

  const item = matches[0];
  const frozenResult = item.result as BacktestResult;
  const setup = item.setup;
  const testedPeriod = exactTestedPeriod(frozenResult);
  const builder = setup.strategyMode === "builder";
  const canonicalDSL = builder ? frozenResult.strategy_builder?.canonical_dsl : setup.strategyMode === "auto" ? executedSharingRecipe(frozenResult) : null;
  const setupDSL = setup.strategyDSL;
  const canonicalIdentity = safeStrategyIdentity(canonicalDSL ?? undefined);
  if (
    !testedPeriod
    || typeof item.id !== "string" || !item.id.trim()
    || !canonicalDSL
    || canonicalDSL.schema_version !== 2
    || !canonicalIdentity
    || builder && canonicalIdentity !== safeStrategyIdentity(setupDSL)
    || !setup.symbol
    || setup.symbol.trim().toUpperCase() !== frozenResult.symbol.trim().toUpperCase()
    || !setup.timeframe
    || setup.timeframe.trim().toLowerCase() !== frozenResult.timeframe.trim().toLowerCase()
    || !frozenAssumptions(setup, testedPeriod)
  ) return null;

  return {
    historyItemID: item.id,
    result: frozenResult,
    setup,
    strategyDSL: canonicalDSL,
    testedPeriod,
  };
}

async function resolvedToken(source: AuthTokenSource): Promise<string> {
  const value = typeof source === "function" ? await source() : source;
  if (!value) throw new Error("share_login_required");
  return value;
}

export function buildShareableResultRequest(
  mode: ShareMode,
  displayedResult: BacktestResult,
  history: readonly HistoryItem[],
  locale: string,
  sharedAt = new Date().toISOString(),
) {
  const frozen = mode === "reproducible_strategy"
    ? resolveFrozenReproducibleShareSource(displayedResult, history)
    : null;
  if (mode === "reproducible_strategy" && !frozen) {
    throw new Error("reproducible_strategy_unavailable");
  }
  const result = frozen?.result || displayedResult;
  const setup = frozen?.setup;
  const testedPeriod = frozen?.testedPeriod || exactTestedPeriod(result);
  const resultCreatedAt = frozen
    ? exactISOInstant(result.generated_at)
    : exactISOInstant(result.generated_at) || sharedAt;
  if (!resultCreatedAt) throw new Error("reproducible_strategy_unavailable");
  const strategyLabel = safeText(
    result.localized_strategy_name || result.strategy_name || setup?.strategyName,
    "Shared strategy",
    72,
  );
  const symbol = safeText(result.symbol, "MARKET", 32);
  const timeframe = safeText(result.timeframe, "1h", 32);
  const localePrefix = locale === "en" ? "" : `/${locale}`;
  const payload = {
    schema_version: 1,
    result_created_at: resultCreatedAt,
    shared_at: sharedAt,
    strategy_label: strategyLabel,
    symbol,
    timeframe,
    tested_period: {
      start: testedPeriod?.start || null,
      end: testedPeriod?.end || null,
    },
    evidence_state: (result.trades?.length || 0) > 0 ? "unvalidated" : "insufficient_evidence",
    metrics: {
      total_return_percent: result.performance.total_profit_percentage ?? null,
      max_drawdown_percent: result.performance.max_drawdown ?? null,
      win_rate_percent: result.performance.win_rate ?? null,
      profit_factor: result.confidence?.sample?.profit_factor ?? null,
      total_trades: Math.min(Math.max(result.trades?.length || 0, 0), 10_000_000),
    },
    equity_curve: normalizedCurve(result),
    methodology_url: `https://backtest.click${localePrefix}/methodology/`,
    risk_note: sharedResultText(resolveSharedResultLocale(locale), "riskNote"),
    omitted_fields: [...OMITTED_FIELDS],
  };
  if (mode === "summary_only") {
    return { locale, mode, expires_in_days: 30, payload };
  }
  const assumptions = frozenAssumptions(frozen!.setup, frozen!.testedPeriod);
  if (!assumptions) throw new Error("reproducible_strategy_unavailable");
  return {
    locale,
    mode,
    expires_in_days: 30,
    payload,
    reproduction: {
      schema_version: 1,
      strategy_dsl: cloneStrategyRuleDSL(frozen!.strategyDSL),
      strategy_label: strategyLabel,
      ...(frozen!.setup.provider != null ? { provider: frozen!.setup.provider } : {}),
      ...(frozen!.setup.market != null ? { market: frozen!.setup.market } : {}),
      symbol,
      timeframe,
      assumptions,
    },
  };
}

export function canShareReproducibleStrategy(
  result: BacktestResult,
  history: readonly HistoryItem[],
): boolean {
  return resolveFrozenReproducibleShareSource(result, history) !== null;
}

export function canPublishProfitableCommunityResult(
  result: BacktestResult,
  history: readonly HistoryItem[],
): boolean {
  const frozen = resolveFrozenReproducibleShareSource(result, history);
  return Boolean(frozen
    && ["5m", "15m", "30m", "1h", "4h", "1d"].includes(frozen.result.timeframe)
    && Number.isFinite(frozen.result.performance?.total_profit_percentage)
    && frozen.result.performance.total_profit_percentage > 0
    && Array.isArray(frozen.result.trades) && frozen.result.trades.length > 0);
}

export interface CommunityShareSourceAttempt {
  readonly accountID: string;
  readonly identity: string;
  readonly sharedAt: string;
  readonly idempotencyKey: string;
  readonly requestBody: string;
}

/** One frozen source and key survive an uncertain network response. */
export function prepareCommunityShareSource(
  accountID: string,
  result: BacktestResult,
  history: readonly HistoryItem[],
  locale: string,
  previous?: CommunityShareSourceAttempt | null,
): CommunityShareSourceAttempt {
  if (!accountID || !canPublishProfitableCommunityResult(result, history)) {
    throw new Error("community_source_not_eligible");
  }
  const sharedAt = previous?.sharedAt || new Date().toISOString();
  const requestBody = JSON.stringify(buildShareableResultRequest(
    "reproducible_strategy", result, history, locale, sharedAt,
  ));
  const identity = JSON.stringify([accountID, requestBody]);
  if (previous?.identity === identity) return previous;
  return Object.freeze({
    accountID, identity, sharedAt, requestBody,
    idempotencyKey: typeof crypto.randomUUID === "function"
      ? crypto.randomUUID() : `community-source-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  });
}

export async function createCommunityShareSource(
  attempt: CommunityShareSourceAttempt,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<string> {
  return withCommunityRequestDeadline(COMMUNITY_READ_TIMEOUT_MS, signal, async (requestSignal) => {
    signal = requestSignal;
    const ensureCurrent = () => {
      if (signal?.aborted || (isCurrent && !isCurrent())) throw new DOMException("Account changed.", "AbortError");
    };
    ensureCurrent();
    const token = await resolvedToken(authToken);
    ensureCurrent();
    const response = await browserSafeFetch(`${API_BASE_URL}/api/v2/shareable-results`, {
      method: "POST", signal,
      headers: {
        "Content-Type": "application/json", Accept: "application/json",
        Authorization: `Bearer ${token}`, "X-Idempotency-Key": attempt.idempotencyKey,
      },
      body: attempt.requestBody, credentials: "omit", cache: "no-store",
      redirect: "error", referrerPolicy: "no-referrer",
    });
    ensureCurrent();
    const value: unknown = await response.json().catch(() => null);
    ensureCurrent();
    const source = isObject(value) && isObject(value.shareable_result) ? value.shareable_result : null;
    if (!response.ok || !source || source.mode !== "reproducible_strategy"
      || typeof source.id !== "string" || !/^shr_[A-Za-z0-9_-]{43}$/.test(source.id)) {
      throw new Error("community_source_create_failed");
    }
    return source.id;
  });
}

export async function createShareableResult(
  mode: ShareMode,
  result: BacktestResult,
  history: readonly HistoryItem[],
  locale: string,
  authToken: AuthTokenSource,
  signal?: AbortSignal,
  isCurrent?: () => boolean,
): Promise<string> {
  const ensureCurrent = () => {
    if (signal?.aborted || isCurrent?.() === false) throw new DOMException("Account changed.", "AbortError");
  };
  ensureCurrent();
  // Serialize the frozen history snapshot before awaiting an auth refresh. A
  // form/history edit while the token resolves can never change this request.
  const requestBody = JSON.stringify(buildShareableResultRequest(
    mode,
    result,
    history,
    locale,
  ));
  const token = await resolvedToken(authToken);
  ensureCurrent();
  const response = await browserSafeFetch(`${API_BASE_URL}/api/v2/shareable-results`, {
    method: "POST", signal,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "X-Idempotency-Key": typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `web-share-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    },
    body: requestBody,
    credentials: "omit",
    cache: "no-store",
    redirect: "error",
    referrerPolicy: "no-referrer",
  });
  ensureCurrent();
  const value = await response.json().catch(() => null) as Record<string, unknown> | null;
  ensureCurrent();
  if (!response.ok || !value || typeof value.shareable_result !== "object" || !value.shareable_result) {
    throw new Error("share_link_create_failed");
  }
  const url = (value.shareable_result as Record<string, unknown>).public_url;
  if (typeof url !== "string" || !/^https:\/\/share\.backtest\.click\/r\/[A-Za-z0-9_-]{43}$/.test(url)) {
    throw new Error("share_link_invalid");
  }
  return url;
}
